/**
 * Maximum length for displayed username (before truncation)
 */
const MAX_USERNAME_LENGTH = 7;

/**
 * Truncates a username to a maximum of 7 characters, appending "..." if longer
 * Handles edge cases: null, undefined, empty string, non-string types
 * 
 * @param {string} username - The username to truncate
 * @returns {string} - Truncated username with "..." if needed, or original if <= 7 chars
 * 
 * @example
 * truncateUsername("NeerajKumar") // Returns "NeerajK..."
 * truncateUsername("Aman") // Returns "Aman"
 * truncateUsername(null) // Returns ""
 * truncateUsername("") // Returns ""
 */
export const truncateUsername = (username) => {
  // Handle edge cases: null, undefined, empty string, non-string
  if (!username || typeof username !== 'string') {
    return '';
  }

  const trimmedUsername = username.trim();
  
  // If username is 7 characters or less, return as is
  if (trimmedUsername.length <= MAX_USERNAME_LENGTH) {
    return trimmedUsername;
  }

  // Truncate to 7 characters and append "..."
  return trimmedUsername.substring(0, MAX_USERNAME_LENGTH) + '...';
};

/**
 * Gets username display data with truncation info
 * Useful when you need both the truncated display and full username for tooltips
 * 
 * @param {string} username - The username to process
 * @returns {Object} - Object with truncated, full, and needsTruncation properties
 * 
 * @example
 * getUsernameDisplay("NeerajKumar")
 * // Returns: { truncated: "NeerajK...", full: "NeerajKumar", needsTruncation: true }
 */
export const getUsernameDisplay = (username) => {
  // Handle edge cases
  if (!username || typeof username !== 'string') {
    return {
      truncated: '',
      full: '',
      needsTruncation: false,
    };
  }

  const trimmedUsername = username.trim();
  const needsTruncation = trimmedUsername.length > MAX_USERNAME_LENGTH;

  return {
    truncated: needsTruncation 
      ? trimmedUsername.substring(0, MAX_USERNAME_LENGTH) + '...'
      : trimmedUsername,
    full: trimmedUsername,
    needsTruncation,
  };
};

